import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { formatLegalOwner } from './propertyFormShared';

interface PropertyCard {
  id: number; address: string; postcode: string; property_type: string;
  bedrooms: number; bathrooms: number; status: string; monthly_rent: number;
  tenant_name: string | null; tenant_email: string | null; tenant_phone: string | null; tenant_id: number | null;
  tenancy_id: number | null; tenancy_start: string | null; tenancy_end: string | null;
  tenancy_status?: string | null; tenancy_ended_at?: string | null; occupancy_active?: boolean | null;
  tenancy_rent: number | null; tenancy_deposit: number | null; tenancy_type: string | null;
  days_to_expiry: number | null; rent_paid: boolean | null; rent_due: number | null; rent_record_id: number | null;
  open_maintenance: number; expiring_certs: number; upcoming_viewings: number;
  room_number?: string | null; total_area_sqm?: number | null; price_per_sqm?: number | null;
  epc_rating?: string | null; council_tax_band?: string | null; furnished?: string; parking?: string; bills_included?: string;
  ready_to_rent?: boolean; gas_safety_expiry?: string | null; eicr_expiry?: string | null;
  legal_owner_title?: string | null; legal_owner_name?: string | null;
}

const UK = { red: '#C8102E', blue: '#012169', white: '#FFFFFF', gold: '#E6A817' };
const FILTERS = ['All', 'Ready to Rent', 'To Let', 'Let', 'Arrears', 'Expiring', 'Maintenance'] as const;

const leaseLifecycleStatus = (row: any) => {
  const raw = String(row?.computed_status || row?.tenancy_status || row?.lease_status || '').toLowerCase();
  if (raw === 'ended' || row?.tenancy_ended_at || row?.ended_at) return 'ended';
  if (raw === 'notice_given') return 'notice_given';
  if (raw === 'expired') return 'expired';
  return 'active';
};

const statusInfo = (p: PropertyCard) => {
  if (!p.tenant_name) {
    if (p.ready_to_rent || p.status === 'ready_to_rent') return { label: 'Ready to Rent', colour: '#16a34a', bg: '#dcfce7' };
    if (p.status === 'under_offer') return { label: 'Let Agreed', colour: '#d97706', bg: '#fef3c7' };
    if (p.status === 'maintenance') return { label: 'Maintenance', colour: '#7c3aed', bg: '#ede9fe' };
    return { label: 'To Let', colour: UK.blue, bg: '#e8edf5' };
  }
  if (p.rent_due && Number(p.rent_due) > 0 && !p.rent_paid) return { label: 'Arrears', colour: UK.red, bg: '#fee2e2' };
  if (leaseLifecycleStatus(p) === 'notice_given') return { label: 'Notice Given', colour: '#b45309', bg: '#fef3c7' };
  if (p.rent_paid) return { label: 'Let · Paid', colour: '#16a34a', bg: '#dcfce7' };
  return { label: 'Let', colour: '#0d9488', bg: '#ccfbf1' };
};

function propertyAccent(group: any, isOccupied: (r: any) => boolean, isExpiring: (r: any) => boolean) {
  if (group.hasArrears) return { color: UK.red, label: 'Arrears' };
  if (Number(group.open_maintenance) > 0) return { color: '#7c3aed', label: 'Maintenance' };
  if (group.rooms.some(isExpiring) || Number(group.expiringCerts) > 0) return { color: UK.gold, label: 'Expiring' };
  const vacant = !group.rooms.some(isOccupied);
  if (vacant && (group.ready_to_rent || group.status === 'ready_to_rent')) return { color: '#16a34a', label: 'Ready to Rent' };
  if (vacant) return { color: UK.blue, label: 'To Let' };
  return { color: '#16a34a', label: 'Let' };
}

export default function Properties() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState<PropertyCard[]>([]);
  const [archivedList, setArchivedList] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState<string>('All');
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isCurrentlyOccupied = (r: any) => {
    const lifecycle = leaseLifecycleStatus(r);
    if (lifecycle === 'ended' || lifecycle === 'expired') return false;
    if (typeof r?.occupancy_active === 'boolean') return r.occupancy_active;
    const hasTenant = !!r?.tenant_name || !!r?.tenancy_id;
    const notExpired = r?.days_to_expiry == null || Number(r.days_to_expiry) >= 0;
    return hasTenant && notExpired;
  };
  const isExpiringLease = (r: any) => {
    if (!isCurrentlyOccupied(r)) return false;
    const days = Number(r?.days_to_expiry);
    return Number.isFinite(days) && days >= 0 && days <= 60;
  };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/landlord/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setCards(d.properties || []);
    } catch { setError('Failed to load properties'); }
    finally { setLoading(false); }
  }, [token]);

  const loadArchived = useCallback(async () => {
    if (!token) return;
    const r = await fetch(`${API_URL}/landlord/properties`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    setArchivedList((d.properties || []).filter((p: any) => p.archived));
  }, [token]);

  useEffect(() => { load(); loadArchived(); }, [load, loadArchived]);

  const restoreProperty = async (id: number) => {
    if (!token) return;
    await fetch(`${API_URL}/landlord/properties/${id}/restore`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    loadArchived(); load();
  };

  const toggleReadyToRent = async (id: number, current: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!token) return;
    await fetch(`${API_URL}/landlord/properties/${id}/ready-to-rent`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ready_to_rent: !current }),
    });
    load();
  };

  const totalRent = cards.filter(c => isCurrentlyOccupied(c)).reduce((s, c) => s + Number(c.tenancy_rent || 0), 0);
  const arrears = cards.filter(c => isCurrentlyOccupied(c) && c.tenant_name && !c.rent_paid && (c.rent_due ?? 0) > 0).length;
  const openIssues = [...new Set(cards.map(c => c.id))].reduce((s, pid) => {
    const c = cards.find(x => x.id === pid)!;
    return s + Number(c.open_maintenance || 0);
  }, 0);

  const uniqueIds = [...new Set(cards.map(c => c.id))];
  const grouped = uniqueIds.map(pid => {
    const rooms = cards.filter(c => c.id === pid);
    const base = rooms[0];
    return {
      ...base, rooms,
      totalRent: rooms.filter((c: any) => isCurrentlyOccupied(c)).reduce((s, c) => s + Number(c.tenancy_rent || 0), 0),
      openMaintenance: base.open_maintenance,
      expiringCerts: base.expiring_certs,
      upcomingViewings: base.upcoming_viewings,
      hasArrears: rooms.some((c: any) => isCurrentlyOccupied(c) && c.tenant_name && !c.rent_paid && (c.rent_due ?? 0) > 0),
    };
  });

  const expiringCount = grouped.filter(g => g.rooms.some((r: any) => isExpiringLease(r)) || Number(g.expiringCerts) > 0).length;
  const readyCount = grouped.filter(g => !g.rooms.some((r: any) => isCurrentlyOccupied(r)) && (g.ready_to_rent || g.status === 'ready_to_rent')).length;
  const alertCount = openIssues + expiringCount;

  const filtered = grouped.filter(g => {
    const q = search.toLowerCase().trim();
    if (q) {
      const isVacant = !g.rooms.some((r: any) => isCurrentlyOccupied(r));
      const isReady = isVacant && (g.ready_to_rent || g.status === 'ready_to_rent');
      const haystack = `${g.address} ${g.postcode} ${g.property_type} ${g.rooms.map((r: any) => r.tenant_name || '').join(' ')}`.toLowerCase();
      const smartMatch = (q === 'vacant' || q === 'void' || q === 'empty') ? isVacant
        : (q === 'ready' || q === 'ready to rent') ? isReady
        : haystack.includes(q);
      if (!smartMatch) return false;
    }
    if (filterTab === 'Ready to Rent') return !g.rooms.some((r: any) => isCurrentlyOccupied(r)) && (g.ready_to_rent || g.status === 'ready_to_rent');
    if (filterTab === 'To Let') return !g.rooms.some((r: any) => isCurrentlyOccupied(r));
    if (filterTab === 'Let') return g.rooms.some((r: any) => isCurrentlyOccupied(r) && r.rent_paid);
    if (filterTab === 'Arrears') return g.hasArrears;
    if (filterTab === 'Expiring') return g.rooms.some((r: any) => isExpiringLease(r)) || Number(g.expiringCerts) > 0;
    if (filterTab === 'Maintenance') return Number(g.open_maintenance) > 0;
    return true;
  });

  const filterCount = (f: string) => {
    if (f === 'Arrears') return arrears;
    if (f === 'Maintenance') return openIssues;
    if (f === 'Expiring') return expiringCount;
    if (f === 'Ready to Rent') return readyCount;
    return 0;
  };

  const CSS = `
    .pr-page { padding: 24px; max-width: 1400px; margin: 0 auto; font-family: 'DM Sans', system-ui, sans-serif; background: #eef2f6; min-height: 100vh; }
    .pr-banner-wrap { margin: -24px -24px 28px; padding: 10px 10px 14px; background: linear-gradient(145deg, #064e3b 0%, #0d9488 38%, #0369a1 72%, #1e40af 100%); border-radius: 0 0 22px 22px; box-shadow: 0 12px 40px rgba(1,33,105,0.22); }
    .pr-banner-glass { display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap; background: rgba(255,255,255,0.11); backdrop-filter: blur(22px); -webkit-backdrop-filter: blur(22px); border: 1px solid rgba(255,255,255,0.28); border-radius: 18px; padding: 34px 38px; box-shadow: 0 8px 32px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.08); }
    .pr-banner-glass h1 { margin: 0; font-size: 34px; font-weight: 800; color: #fff; letter-spacing: -0.03em; text-shadow: 0 2px 12px rgba(0,0,0,0.2); }
    .pr-banner-glass p { margin: 8px 0 0; font-size: 14px; color: rgba(255,255,255,0.88); font-weight: 500; }
    .pr-banner-actions { display: flex; gap: 10px; flex-shrink: 0; }
    .pr-btn-glass { background: rgba(255,255,255,0.14); color: #fff; border: 1px solid rgba(255,255,255,0.35); border-radius: 10px; padding: 11px 20px; font-weight: 700; font-size: 13px; cursor: pointer; backdrop-filter: blur(6px); transition: background .15s; }
    .pr-btn-glass:hover { background: rgba(255,255,255,0.24); }
    .pr-btn-add { background: #fff; color: ${UK.blue}; border: none; border-radius: 10px; padding: 11px 24px; font-weight: 800; font-size: 14px; cursor: pointer; box-shadow: 0 4px 16px rgba(0,0,0,0.14); transition: transform .12s; }
    .pr-btn-add:hover { transform: translateY(-2px); }
    .pr-kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
    .pr-kpi { border-radius: 16px; padding: 26px 24px 22px; position: relative; overflow: hidden; transition: transform .15s, box-shadow .15s; min-height: 118px; }
    .pr-kpi:hover { transform: translateY(-4px); }
    .pr-kpi-blue { background: linear-gradient(145deg, ${UK.blue}, #1e3a8a); box-shadow: 0 10px 28px rgba(1,33,105,0.35); }
    .pr-kpi-white { background: ${UK.white}; border: 1.5px solid #e2e8f0; box-shadow: 0 8px 24px rgba(0,0,0,0.06); }
    .pr-kpi-red { background: linear-gradient(145deg, ${UK.red}, #ef4444); box-shadow: 0 10px 28px rgba(200,16,46,0.32); }
    .pr-kpi-gold { background: linear-gradient(145deg, #ca8a04, ${UK.gold}); box-shadow: 0 10px 28px rgba(230,168,23,0.35); }
    .pr-kpi .kpi-label { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.09em; margin-bottom: 8px; }
    .pr-kpi-blue .kpi-label, .pr-kpi-red .kpi-label, .pr-kpi-gold .kpi-label { color: rgba(255,255,255,0.85); }
    .pr-kpi-white .kpi-label { color: #64748b; }
    .pr-kpi .kpi-val { font-size: 32px; font-weight: 800; line-height: 1.1; font-family: 'DM Mono', monospace; }
    .pr-kpi-blue .kpi-val, .pr-kpi-red .kpi-val, .pr-kpi-gold .kpi-val { color: #fff; }
    .pr-kpi-white .kpi-val { color: ${UK.blue}; }
    .pr-kpi .kpi-sub { font-size: 12px; margin-top: 6px; font-weight: 500; }
    .pr-kpi-blue .kpi-sub, .pr-kpi-red .kpi-sub, .pr-kpi-gold .kpi-sub { color: rgba(255,255,255,0.75); }
    .pr-kpi-white .kpi-sub { color: #94a3b8; }
    .pr-toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 22px; flex-wrap: wrap; }
    .pr-search { display: flex; align-items: center; gap: 10px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 10px 16px; flex: 1; min-width: 220px; max-width: 420px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
    .pr-search input { border: none; outline: none; font-size: 14px; color: #111; background: transparent; width: 100%; font-family: inherit; }
    .pr-search input::placeholder { color: #94a3b8; }
    .pr-filter-select { padding: 10px 36px 10px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 8L1 3h10z'/%3E%3C/svg%3E") no-repeat right 12px center; font-size: 13px; font-weight: 700; color: ${UK.blue}; cursor: pointer; appearance: none; min-width: 200px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
    .pr-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 16px; }
    .pr-card { background: #fff; border-radius: 12px; border: 1px solid #e5e7eb; box-shadow: 0 2px 10px rgba(0,0,0,0.04); display: flex; flex-direction: column; overflow: hidden; transition: box-shadow .15s, transform .15s; }
    .pr-card:hover { box-shadow: 0 10px 28px rgba(1,33,105,0.1); transform: translateY(-2px); }
    .pr-card-accent { height: 4px; width: 100%; flex-shrink: 0; }
    .pr-card-body { padding: 18px 18px 14px; flex: 1; display: flex; flex-direction: column; }
    .pr-card-head { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
    .pr-card-addr { font-size: 15px; font-weight: 800; color: #111; line-height: 1.3; }
    .pr-card-meta { font-size: 12px; color: #6b7280; margin-top: 4px; }
    .pr-card-rent { font-size: 20px; font-weight: 800; color: ${UK.blue}; white-space: nowrap; font-family: 'DM Mono', monospace; }
    .pr-card-rent span { font-size: 11px; font-weight: 500; color: #9ca3af; }
    .pr-status-pill { display: inline-block; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; padding: 3px 9px; border-radius: 5px; margin-top: 6px; }
    .pr-occupancy { background: #f8fafc; border: 1px solid #e8edf5; border-radius: 8px; padding: 8px 11px; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 10px; }
    .pr-room-row { display: flex; align-items: center; gap: 8px; padding: 7px 10px; background: #fafbfc; border-radius: 7px; margin-bottom: 5px; font-size: 12px; }
    .pr-room-name { flex: 1; font-weight: 600; color: #111; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .pr-room-badge { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px; white-space: nowrap; }
    .pr-alerts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; min-height: 20px; }
    .pr-alert { font-size: 11px; font-weight: 600; color: #64748b; }
    .pr-card-foot { display: flex; gap: 8px; padding: 12px 18px 16px; border-top: 1px solid #f1f5f9; }
    .pr-btn-view { flex: 1; padding: 9px 14px; border-radius: 8px; border: 1.5px solid #d1d5db; background: #fff; color: #111; font-weight: 700; font-size: 13px; cursor: pointer; }
    .pr-btn-view:hover { border-color: ${UK.blue}; color: ${UK.blue}; }
    .pr-btn-edit { flex: 1; padding: 9px 14px; border-radius: 8px; border: none; background: #16a34a; color: #fff; font-weight: 700; font-size: 13px; cursor: pointer; }
    .pr-btn-edit:hover { background: #15803d; }
    .pr-btn-ready { font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 6px; border: none; cursor: pointer; margin-top: 6px; }
    @media (max-width: 900px) { .pr-kpi-row { grid-template-columns: repeat(2, 1fr); } .pr-banner-glass h1 { font-size: 26px; } }
    @media (max-width: 520px) { .pr-kpi-row { grid-template-columns: 1fr; } }
  `;

  return (
    <div className="pr-page">
      <style>{CSS}</style>

      <div className="pr-banner-wrap">
        <div className="pr-banner-glass">
          <div>
            <h1>Properties</h1>
            <p>Portfolio overview — use View or Edit on each property. No side panel.</p>
          </div>
          <div className="pr-banner-actions">
            <button type="button" className="pr-btn-glass" onClick={() => setShowArchived(a => !a)}>
              {showArchived ? 'Hide Archived' : `Archived${archivedList.length > 0 ? ` (${archivedList.length})` : ''}`}
            </button>
            <button type="button" className="pr-btn-add" onClick={() => navigate('/landlord/properties/new')}>
              + Add Property
            </button>
          </div>
        </div>
      </div>

      <div className="pr-kpi-row">
        <div className="pr-kpi pr-kpi-blue">
          <div className="kpi-label">Portfolio</div>
          <div className="kpi-val">{grouped.length}</div>
          <div className="kpi-sub">properties active</div>
        </div>
        <div className="pr-kpi pr-kpi-white">
          <div className="kpi-label">Monthly Rent Roll</div>
          <div className="kpi-val">£{totalRent.toLocaleString()}</div>
          <div className="kpi-sub">collected this month</div>
        </div>
        <div className="pr-kpi pr-kpi-red">
          <div className="kpi-label">In Arrears</div>
          <div className="kpi-val">{arrears}</div>
          <div className="kpi-sub">{arrears > 0 ? 'overdue rent' : 'all clear'}</div>
        </div>
        <div className="pr-kpi pr-kpi-gold">
          <div className="kpi-label">Alerts</div>
          <div className="kpi-val">{alertCount}</div>
          <div className="kpi-sub">certs, leases & repairs</div>
        </div>
      </div>

      <div className="pr-toolbar">
        <div className="pr-search">
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search address, postcode, tenant, vacant…" />
        </div>
        <select className="pr-filter-select" value={filterTab} onChange={e => setFilterTab(e.target.value)}>
          {FILTERS.map(f => {
            const n = filterCount(f);
            return <option key={f} value={f}>{f}{n > 0 && f !== 'All' ? ` (${n})` : ''}</option>;
          })}
        </select>
      </div>

      {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: UK.red, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontWeight: 600 }}>{error}</div>}

      {loading ? (
        <p style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Loading properties…</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 12, border: '2px dashed #e5e7eb' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#374151' }}>{cards.length === 0 ? 'No properties yet' : 'No matching properties'}</div>
          <div style={{ color: '#6b7280', marginTop: 8 }}>{cards.length === 0 ? 'Add your first property to get started' : 'Try a different search or filter'}</div>
          {cards.length === 0 && (
            <button type="button" onClick={() => navigate('/landlord/properties/new')} style={{ marginTop: 16, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 700, cursor: 'pointer' }}>+ Add Property</button>
          )}
        </div>
      ) : (
        <div className="pr-grid">
          {filtered.map(group => {
            const accent = propertyAccent(group, isCurrentlyOccupied, isExpiringLease);
            const occupiedCount = group.rooms.filter((r: any) => isCurrentlyOccupied(r)).length;
            const totalRooms = Math.max(Number(group.bedrooms) || 0, group.rooms.length || 0, occupiedCount || 1);
            const vacantRooms = Math.max(0, totalRooms - occupiedCount);
            const isVacant = occupiedCount === 0;
            const isReady = isVacant && !!(group.ready_to_rent || group.status === 'ready_to_rent');

            return (
              <div key={group.id} className="pr-card">
                <div className="pr-card-accent" style={{ background: accent.color }} title={accent.label} />
                <div className="pr-card-body">
                  <div className="pr-card-head">
                    <div style={{ minWidth: 0 }}>
                      <div className="pr-card-addr">{group.address}</div>
                      <div className="pr-card-meta">{group.postcode} · {group.property_type}{group.total_area_sqm ? ` · ${group.total_area_sqm}m²` : ''}</div>
                      {formatLegalOwner(group.legal_owner_title, group.legal_owner_name) && (
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 6 }}>
                          <strong style={{ color: UK.blue }}>Legal landlord:</strong> {formatLegalOwner(group.legal_owner_title, group.legal_owner_name)}
                        </div>
                      )}
                      <span className="pr-status-pill" style={{ background: accent.color + '18', color: accent.color }}>{accent.label}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="pr-card-rent">
                        £{(group.totalRent > 0 ? group.totalRent : Number(group.monthly_rent)).toLocaleString()}<span>/mo</span>
                      </div>
                    </div>
                  </div>

                  <div className="pr-occupancy">
                    {occupiedCount} of {totalRooms} rooms let · {vacantRooms} vacant
                  </div>

                  <div style={{ flex: 1 }}>
                    {group.rooms.slice(0, 3).map((room: any, i: number) => {
                      const occupied = isCurrentlyOccupied(room);
                      const si = statusInfo(occupied ? room : { ...room, tenant_name: null, rent_paid: null, rent_due: null });
                      return (
                        <div key={room.tenancy_id ?? room.tenant_id ?? i} className="pr-room-row">
                          <span className="pr-room-name">{occupied ? room.tenant_name : 'Vacant'}</span>
                          {room.tenancy_rent && occupied && <span style={{ color: '#64748b', fontSize: 11 }}>£{Number(room.tenancy_rent).toLocaleString()}</span>}
                          <span className="pr-room-badge" style={{ background: si.bg, color: si.colour }}>{si.label}</span>
                        </div>
                      );
                    })}
                    {group.rooms.length > 3 && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>+{group.rooms.length - 3} more rooms</div>}
                  </div>

                  <div className="pr-alerts">
                    {Number(group.openMaintenance) > 0 && <span className="pr-alert" style={{ color: '#ea580c' }}>{group.openMaintenance} repair{group.openMaintenance > 1 ? 's' : ''}</span>}
                    {Number(group.expiringCerts) > 0 && <span className="pr-alert" style={{ color: UK.gold }}>{group.expiringCerts} cert{group.expiringCerts > 1 ? 's' : ''} expiring</span>}
                    {Number(group.upcomingViewings) > 0 && <span className="pr-alert" style={{ color: UK.blue }}>{group.upcomingViewings} viewing{group.upcomingViewings > 1 ? 's' : ''}</span>}
                    {isVacant && (
                      <button type="button" className="pr-btn-ready" onClick={e => toggleReadyToRent(group.id, isReady, e)}
                        style={{ background: isReady ? '#dcfce7' : '#f1f5f9', color: isReady ? '#16a34a' : '#64748b' }}>
                        {isReady ? 'Ready to Rent' : 'Mark Ready'}
                      </button>
                    )}
                  </div>
                </div>

                <div className="pr-card-foot">
                  <button type="button" className="pr-btn-view" onClick={() => navigate(`/landlord/properties/${group.id}`)}>View</button>
                  <button type="button" className="pr-btn-edit" onClick={() => navigate(`/landlord/properties/${group.id}/edit`)}>Edit</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showArchived && (
        <div style={{ marginTop: 36, borderTop: '2px dashed #e2e8f0', paddingTop: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
            Archived Properties ({archivedList.length})
          </div>
          {archivedList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', fontSize: 14 }}>
              No archived properties — archive from a property view page
            </div>
          ) : (
            <div className="pr-grid">
              {archivedList.map(p => (
                <div key={p.id} className="pr-card">
                  <div className="pr-card-accent" style={{ background: '#94a3b8' }} />
                  <div className="pr-card-body">
                    <div className="pr-card-addr">{p.address}</div>
                    <div className="pr-card-meta">{p.postcode} · {p.property_type} · £{Number(p.monthly_rent).toLocaleString()}/mo</div>
                  </div>
                  <div className="pr-card-foot">
                    <button type="button" className="pr-btn-edit" style={{ background: UK.blue }} onClick={() => restoreProperty(p.id)}>Restore</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
